import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, Minus, ArrowRight, Shield, Target, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';

const ServiceSelector = () => {
  const [selectedService, setSelectedService] = useState('investment');
  const [isHovered, setIsHovered] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const carouselRef = useRef(null);

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = {
    investment: {
      icon: TrendingUp,
      title: "Investment Planning",
      description: "Strategic tax-efficient investment solutions",
      features: [
        "Personalized portfolio structuring",
        "Tax-loss harvesting strategies",
        "Strategic asset location"
      ],
      gradient: "from-[#113262] to-[#1C52A0]"
    },
    estate: {
      icon: Shield,
      title: "Estate Planning",
      description: "Comprehensive wealth transfer strategies",
      features: [
        "Trust structuring & optimization",
        "Generational wealth planning",
        "Tax-efficient succession"
      ],
      gradient: "from-[#1C52A0] to-[#436FB0]"
    },
    retirement: {
      icon: Target,
      title: "Retirement Planning",
      description: "Long-term retirement optimization",
      features: [
        "Tax-optimized withdrawal strategies",
        "Pension & benefits planning",
        "Income stream diversification"
      ],
      gradient: "from-[#436FB0] to-[#7B9BC8]"
    }
  };

  const serviceArray = Object.entries(services);
  const ServiceIcon = services[selectedService].icon;

  // Find the current service index for carousel navigation
  const currentIndex = serviceArray.findIndex(([key]) => key === selectedService);

  // Carousel navigation functions
  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % serviceArray.length;
    setSelectedService(serviceArray[nextIndex][0]);
  };

  const goToPrev = () => {
    const prevIndex = (currentIndex - 1 + serviceArray.length) % serviceArray.length;
    setSelectedService(serviceArray[prevIndex][0]);
  };

  // Determine if we should show mobile layout
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden backdrop-blur-lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {/* Service selection sidebar - visible on large screens, hidden on smaller screens */}
          <div className={`${isMobile || isTablet ? 'hidden' : 'block'} lg:border-r border-gray-100`}>
            {Object.entries(services).map(([key, service]) => {
              const Icon = service.icon;
              return (
                <button
                  key={key}
                  className={`w-full p-6 text-left transition-all duration-300 relative ${selectedService === key
                      ? 'bg-gradient-to-r from-[#E8EEF6] to-white'
                      : 'hover:bg-gray-50'
                    }`}
                  onClick={() => setSelectedService(key)}
                  onMouseEnter={() => setIsHovered(key)}
                  onMouseLeave={() => setIsHovered(null)}
                >
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg ${selectedService === key
                        ? 'bg-gradient-to-br ' + service.gradient
                        : 'bg-[#E8EEF6]'
                      }`}>
                      <Icon className={`h-6 w-6 ${selectedService === key ? 'text-white' : 'text-[#1C52A0]'
                        }`} />
                    </div>
                    <div className="ml-4">
                      <h3 className={`font-semibold transition-colors ${selectedService === key ? 'text-[#113262]' : 'text-gray-600'
                        }`}>
                        {service.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  {selectedService === key && (
                    <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-[#1C52A0] to-[#436FB0]" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile/Tablet carousel for service selection */}
          {(isMobile || isTablet) && (
            <div className="p-4 border-b border-gray-100" ref={carouselRef}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 text-center">Our Services</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={goToPrev}
                    className="p-1 rounded-full bg-[#E8EEF6] hover:bg-[#D8DEE6] transition-colors"
                    aria-label="Previous service"
                  >
                    <ChevronLeft className="h-5 w-5 text-[#113262]" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="p-1 rounded-full bg-[#E8EEF6] hover:bg-[#D8DEE6] transition-colors"
                    aria-label="Next service"
                  >
                    <ChevronRight className="h-5 w-5 text-[#113262]" />
                  </button>
                </div>
              </div>

              <div className="relative overflow-hidden">
                <div
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {Object.entries(services).map(([key, service]) => {
                    const Icon = service.icon;
                    return (
                      <div
                        key={key}
                        className={`min-w-full p-4 ${selectedService === key ? 'bg-gradient-to-r from-[#E8EEF6] to-white' : ''}`}
                      >
                        <button
                          onClick={() => setSelectedService(key)}
                          className={`w-full flex items-center p-3 rounded-lg border-2 ${selectedService === key
                              ? 'border-[#1C52A0] bg-white shadow-md'
                              : 'border-transparent hover:border-gray-200'
                            }`}
                        >
                          <div className={`p-2 rounded-lg ${selectedService === key
                              ? 'bg-gradient-to-br ' + service.gradient
                              : 'bg-[#E8EEF6]'
                            }`}>
                            <Icon className={`h-5 w-5 ${selectedService === key ? 'text-white' : 'text-[#1C52A0]'
                              }`} />
                          </div>
                          <div className="ml-3 text-left">
                            <h3 className={`font-semibold text-sm ${selectedService === key ? 'text-[#113262]' : 'text-gray-600'
                              }`}>
                              {service.title}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                              {service.description}
                            </p>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination dots */}
              <div className="flex justify-center mt-3 space-x-2">
                {serviceArray.map(([key], index) => (
                  <button
                    key={key}
                    onClick={() => setSelectedService(key)}
                    className={`h-2 w-2 rounded-full transition-all ${currentIndex === index ? 'bg-[#1C52A0] w-4' : 'bg-gray-300'
                      }`}
                    aria-label={`Go to ${services[key].title}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Service details - spans full width on mobile/tablet, 2/3 on desktop */}
          <div className={`${isMobile || isTablet ? 'col-span-1' : 'col-span-2'} p-6 md:p-8 bg-gradient-to-br from-white to-[#F8FAFC]`}>
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center">
                <div className="p-3 md:p-4 bg-gradient-to-br from-[#113262] to-[#1C52A0] rounded-xl md:rounded-2xl">
                  <ServiceIcon className="h-6 w-6 md:h-8 md:w-8 text-white" />
                </div>
                <h2 className="ml-4 md:ml-6 text-xl md:text-2xl font-bold text-[#113262]">
                  {services[selectedService].title}
                </h2>
              </div>

              <p className="mt-4 md:mt-6 text-sm md:text-base text-gray-600 leading-relaxed">
                {services[selectedService].description}
              </p>

              <div className="mt-6 md:mt-8 space-y-3 md:space-y-4">
                {services[selectedService].features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 md:p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="h-2 w-2 rounded-full bg-[#1C52A0]" />
                    <span className="ml-3 md:ml-4 text-sm md:text-base text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Mobile & tablet navigation controls at the bottom */}
              {(isMobile || isTablet) && (
                <div className="flex justify-between mt-8">
                  <button
                    onClick={goToPrev}
                    className="flex items-center px-4 py-2 bg-[#E8EEF6] rounded-lg text-[#113262] hover:bg-[#D8DEE6] transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    <span className="text-sm">Previous</span>
                  </button>
                  <button
                    onClick={goToNext}
                    className="flex items-center px-4 py-2 bg-[#113262] rounded-lg text-white hover:bg-[#0A255A] transition-colors"
                  >
                    <span className="text-sm">Next</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full py-6 flex justify-between items-center text-left"
        onClick={onClick}
      >
        <h3 className="text-lg font-semibold text-[#113262] pr-8">{question}</h3>
        <ChevronDown
          className={`w-5 h-5 text-[#F49611] transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'
          }`}
      >
        <p className="text-gray-600">{answer}</p>
      </div>
    </div>
  );
};

const FAQAccordion = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      id: 1,
      category: "Investment",
      question: "How can tax-efficient investing enhance my returns?",
      answer: "Tax-efficient investing strategically minimizes tax impact while maximizing returns through careful asset location, loss harvesting, and timing of realizations. This approach can significantly improve long-term after-tax wealth accumulation."
    },
    {
      id: 2,
      category: "Strategy",
      question: "What are the key tax planning strategies for high net worth individuals?",
      answer: "Our comprehensive approach includes strategic asset allocation, tax-loss harvesting, charitable giving strategies, and estate planning optimization, all tailored to your specific financial situation and goals."
    },
    {
      id: 3,
      category: "Process",
      question: "How frequently should tax planning strategies be reviewed?",
      answer: "We conduct quarterly strategy reviews, with comprehensive annual assessments and additional reviews triggered by significant life events or tax law changes to ensure optimal alignment with your goals."
    }
  ];

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="flex flex-col md:flex-row gap-16">
        <div className="md:w-1/3">
          <div className="sticky top-8">
            <div className="w-12 h-0.5 bg-[#F49611] mb-3"></div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
              Common Questions About Tax Planning
            </h2>
            <p className="text-gray-600 mb-8">
              Get answers to frequently asked questions about our Tax Planning Service.
              For more detailed discussions, our investment experts are ready to help.
            </p>
            <a href="/meeting" className="flex items-center text-[#F49611] font-semibold hover:gap-2 transition-all">
              <span>Speak With An Expert</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
        <div className="md:w-2/3">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={index === openIndex}
              onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQAccordion;

export { ServiceSelector, FAQAccordion };